import { GridFilterItem } from '@mui/x-data-grid-pro';
import { useEffect, useMemo } from 'react';

import { createColumnDependencies } from 'components/ModelList/columns';
import {
  FilterIds,
  FilterOperators,
} from 'components/ModelList/components/FilterPanel';
import { useURLSearchParams } from 'hooks/useURLSearchParams';

const dependencies = createColumnDependencies((columnIds) => [columnIds.NAME]);

/**
 * @param dateColumnId - A string containing the date column id to filter (only needed when the consuming component filters by date).
 * @param isDate - flag: date mode (use dateColumnId in URL)
 */
export const useURLFilters = (dateColumnId?: string, isDate?: boolean) => {
  const [NAME] = dependencies;
  const [queryParams, setQueryParams] = useURLSearchParams();
  const filterText = queryParams[FilterIds.NAME];
  const startDate = queryParams[FilterIds.START_DATE];
  const endDate = queryParams[FilterIds.END_DATE];
  const filteredDateColumnId = queryParams.dateColumnId;

  useEffect(() => {
    if (isDate && dateColumnId && dateColumnId !== filteredDateColumnId) {
      setQueryParams({ dateColumnId });
    }
  }, [filteredDateColumnId, dateColumnId, isDate, setQueryParams]);

  const updateFilterModelItems = (
    newQueryParams: Partial<Record<FilterIds, string | null>>
  ) => {
    setQueryParams(newQueryParams);
  };

  const filters: GridFilterItem[] = useMemo(() => {
    const items: GridFilterItem[] = [];

    if (filterText) {
      items.push({
        field: NAME ?? '',
        id: FilterIds.NAME,
        operator: FilterOperators.CONTAINS,
        value: filterText,
      });
    }

    if (filteredDateColumnId) {
      if (startDate) {
        items.push({
          field: filteredDateColumnId,
          id: FilterIds.START_DATE,
          operator: FilterOperators.ON_OR_AFTER,
          value: startDate,
        });
      }

      if (endDate) {
        items.push({
          field: filteredDateColumnId,
          id: FilterIds.END_DATE,
          operator: FilterOperators.ON_OR_BEFORE,
          value: endDate,
        });
      }
    }

    return items;
  }, [NAME, filteredDateColumnId, endDate, filterText, startDate]);

  return { filters, updateFilterModelItems };
};
